import axios, { AxiosHeaders, CanceledError } from "axios";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import { AccountType } from "users/account-types/account-type";
import Guid from "_shared/guids/guid";
import Session from "users/__session/session";
import WorkTeamTemplateQuery from "work/team-templates/_api/queries/team-template-query";
import UpdateTeamTemplateAPIRequest from "work/team-templates/_api/commands/update-team-template-api-request";
import TeamTemplateAPIRequest from "work/team-templates/_api/commands/team-template-api-request";
import TeamTemplate from "work/team-templates/team-template";
import VendorTeamName from "work/work-agreements/teams/vendor-team/vendor-team-name/vendor-team-name";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import TeamTemplateSchema, { TeamTemplateJSON } from "work/team-templates/_schema/team-template-schema";

export default class TeamTemplateAPIService {
  private readonly authHeaders: AxiosHeaders = new AxiosHeaders();

  private authHeadersWithJson(): AxiosHeaders {
    return this.authHeaders.concat({ "Content-Type": "application/json-patch+json" });
  }

  constructor(session: Readonly<Session>) {
    if (!session.authToken?.value) throw new Error("Session must have an authToken to create a TeamTemplateAPIService");
    this.authHeaders.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async getTeamTemplates(
    query: WorkTeamTemplateQuery,
    abortController?: AbortController,
  ): Promise<PaginatedResponse<TeamTemplateJSON>> {
    const url = new URL(`/work/team-templates?${query.searchParams}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });

      const responseData = response.data.map((template: unknown) => TeamTemplateSchema.validateJSON(template));

      return PaginatedResponse.from(responseData, response.headers["x-pagination"]);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new TeamTemplateAPIServiceError("getTeamTemplates", error);
    }
  }

  async createTeamTemplate(
    entityId: Guid,
    team: VendorTeam,
    name: VendorTeamName,
    creatorType: AccountType,
  ): Promise<TeamTemplateJSON> {
    const url = new URL("/work/team-templates", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(url.toString(), new TeamTemplateAPIRequest(entityId, team, name, creatorType), {
        headers: this.authHeadersWithJson(),
      });

      return TeamTemplateSchema.validateJSON(response.data);
    } catch (error) {
      throw new TeamTemplateAPIServiceError("createTeamTemplate", error);
    }
  }

  async updateTeamTemplate(
    originalTemplate: TeamTemplate,
    updatedTeam: VendorTeam,
    updatedTeamName: VendorTeamName,
  ): Promise<TeamTemplateJSON> {
    try {
      const url = new URL(`/work/team-templates/${originalTemplate.id?.value}`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.patch(
        url.toString(),
        new UpdateTeamTemplateAPIRequest(originalTemplate, updatedTeam, updatedTeamName).payload,
        {
          headers: this.authHeadersWithJson(),
        },
      );

      return TeamTemplateSchema.validateJSON(response.data);
    } catch (error) {
      throw new TeamTemplateAPIServiceError("updateTeamTemplate", error);
    }
  }

  async deleteTeamTemplate(templateId: Guid): Promise<void> {
    try {
      const url = new URL(`/work/team-templates/${templateId.value}`, AttorneyHubAPIService.apiBaseUrl);
      await axios.delete(url.toString(), { headers: this.authHeaders });
    } catch (error) {
      throw new TeamTemplateAPIServiceError("deleteTeamTemplate", error);
    }
  }

  async registerTeamTemplateToMarketplace(templateId: Guid): Promise<TeamTemplateJSON> {
    try {
      const url = new URL(
        `/work/team-templates/${templateId.value}/register-to-marketplace`,
        AttorneyHubAPIService.apiBaseUrl,
      );
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders,
        },
      );

      return TeamTemplateSchema.validateJSON(response.data);
    } catch (error) {
      throw new TeamTemplateAPIServiceError("registerTeamTemplateToMarketplace", error);
    }
  }
}

export class TeamTemplateAPIServiceError extends Error {
  constructor(
    public method: string,
    public error: unknown,
  ) {
    super(`Error in TeamTemplateAPIService.${method}: ${error}`);
    this.name = "TeamTemplateAPIServiceError";
  }
}
